<template>
  <div class="flex flex-col h-full">
    <div class="flex-1">
      <BaseTable
        v-if="representativeImports"
        :headers="tableHeaders"
        :list="filteredRepresentativeImports"
        table-id="representative-imports"
        is-saving-layout-hidden
        clickable
        hide-grouping
        :is-add-new-hidden="mode === 'view'"
        :disable-multiselect="mode === 'view'"
        is-refresh-button-hidden
        @add-new="onAddNew"
        @table-item-clicked="onEdit"
        @delete-multiselect="onDeleteMultiselect">
        <template #actions="{ item }">
          <div class="flex items-center justify-center gap-1">
            <button
              class="p-1.5 text-blue-400 rounded-full hover:bg-blue-50"
              type="button"
              title="Record Mapping Settings"
              @click="onOpenRecordMapping(item)">
              <img src="@/assets/images/settings-blue.svg" class="w-4 h-4" alt="Settings" />
            </button>
            <button
              v-if="item.isFromManager && mode == 'edit'"
              class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
              type="button"
              @click="onDeleteRow(item)">
              <IconTrashOutline class="w-4 h-4" />
            </button>
          </div>
        </template>
      </BaseTable>

      <DCRepresentativeImportModalCreateEdit
        v-if="createEditModalActive && currentRepresentativeImport"
        :representative-import="currentRepresentativeImport"
        :mode="createEditModalMode"
        :view-only="mode === 'view'"
        :representative-id="representativeId"
        @close-modal="createEditModalActive = false"
        @save="onSave" />

      <RepresentativeImportRecordMappingCreateEdit
        v-if="recordMappingModalActive"
        :record-mapping="currentRecordMapping"
        :representative-id="representativeId"
        :representative-code="representativeCode"
        :mode="mode"
        @close-modal="recordMappingModalActive = false"
        @save="onSaveRecordMapping" />

      <BaseModalConfirm
        v-if="modalDeleteRow"
        @cancel="modalDeleteRow = false"
        @confirm="onDeleteSingle()" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import BaseTable from '@/components/common/BaseTable.vue';
import DCRepresentativeImportModalCreateEdit from '@/components/traders/DCRepresentativeImportModalCreateEdit.vue';
import RepresentativeImportRecordMappingCreateEdit from '@/components/traders/RepresentativeImportRecordMappingCreateEdit.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';

import { RecordMode } from '@/types/Base';
import { deepCopy } from '@/utils/data';
import { filterData, sortData } from '@/utils/table';
import type { DCRepresentativeImport } from '@/types/DataCollection';
import { getDCRepresentativeImports } from '@/services/api/dataCollection';
import { isoDateToFullDate } from '@/utils/date';

const props = defineProps<{
  mode: 'view' | 'edit';
  resetState: boolean;
  representativeId: string;
  representativeCode: string;
}>();

const representativeImports = defineModel<DCRepresentativeImport[]>('representativeImports');
const representativeImportModified = defineModel<boolean>('isRepresentativeImportModified', {
  default: false,
});

const originalRepresentativeImports = ref<DCRepresentativeImport[]>([]);

watch(
  () => representativeImports.value,
  (newData) => {
    representativeImportModified.value =
      JSON.stringify(newData) !== JSON.stringify(originalRepresentativeImports.value);
  },
  { deep: true }
);

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (newVal && props.representativeId && (!oldVal || oldVal !== newVal))
      await loadRepresentativeImports();
  },
  { immediate: true }
);

const { t: $t } = useI18n();
onMounted(() => {
  if (props.resetState == false && props.representativeId) loadRepresentativeImports();
});

async function loadRepresentativeImports() {
  const res = await getDCRepresentativeImports(props.representativeId);
  if (res) {
    originalRepresentativeImports.value = res.map((item) => ({
      ...item,
      lastImportDateString: isoDateToFullDate(item.lastImportDate) || '',
    }));
    representativeImports.value = deepCopy<DCRepresentativeImport[]>(
      originalRepresentativeImports.value
    );
  }
}

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');
const currentRepresentativeImport = ref<DCRepresentativeImport | undefined>(undefined);

// Record mapping modal state
const recordMappingModalActive = ref(false);
const currentRecordMapping = ref<string>('');

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'remarks', text: $t('representative.remarks'), type: 'text', active: true },
  {
    value: 'lastImportDateString',
    text: $t('representative.lastImportDate'),
    type: 'text',
    active: true,
  },
  { value: 'importFolder', text: $t('representative.importFolder'), type: 'text', active: true },
  { value: 'importUrl', text: $t('representative.importUrl'), type: 'text', active: true },
  {
    value: 'importFileNameFormat',
    text: $t('representative.importFileNameFormat'),
    type: 'text',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const filteredRepresentativeImports = computed<DCRepresentativeImport[]>(() => {
  if (!representativeImports.value) return [];
  let list = representativeImports.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  if (filterState.query) {
    list = filterData(list, filterState.query, [
      'code',
      'name',
      'remarks',
      'importFolder',
      'importUrl',
    ]);
  }

  if (filterState.column !== undefined && filterState.order) {
    list = sortData(list, filterState.column, filterState.order, tableHeaders);
  }

  return list;
});

function initRepresentativeImport(): DCRepresentativeImport {
  return {
    id: uuidv4(),
    representativeId: props.representativeId,
    code: '',
    name: '',
    remarks: '',
    lastImportDate: undefined,
    importFolder: '',
    importUrl: '',
    importFileNameFormat: '',
    recordMapping: '',
    isActive: true,
    isFromManager: true,
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentRepresentativeImport.value = initRepresentativeImport();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(item: DCRepresentativeImport) {
  currentRepresentativeImport.value = deepCopy(item);
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

function onSave(importItem: DCRepresentativeImport) {
  if (!representativeImports.value) return;
  createEditModalActive.value = false;
  recordMappingModalActive.value = false;

  const existingIndex = representativeImports.value?.findIndex((x) => x.id === importItem.id);
  if (existingIndex !== -1)
    representativeImports.value[existingIndex] = {
      ...importItem,
      lastImportDateString: importItem.lastImportDate
        ? isoDateToFullDate(importItem.lastImportDate)
        : '',
    };
  else representativeImports.value.push(importItem);
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCRepresentativeImport>();

function onDeleteRow(item: DCRepresentativeImport) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (deleteRowItem.value && representativeImports.value) {
    const index = representativeImports.value.findIndex((x) => x.id === deleteRowItem.value!.id);
    if (index >= 0) {
      if (representativeImports.value[index].recordMode === RecordMode.Added) {
        representativeImports.value.splice(index, 1);
      } else {
        representativeImports.value[index].recordMode = RecordMode.Deleted;
      }
    }
  }
  modalDeleteRow.value = false;
  deleteRowItem.value = undefined;
}

function onDeleteMultiselect(items: DCRepresentativeImport[]) {
  if (!representativeImports.value) return;

  items.forEach((item) => {
    const index = representativeImports.value!.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      if (representativeImports.value![index].recordMode === RecordMode.Added) {
        representativeImports.value!.splice(index, 1);
      } else {
        representativeImports.value![index].recordMode = RecordMode.Deleted;
      }
    }
  });
}

function onOpenRecordMapping(item: DCRepresentativeImport) {
  currentRepresentativeImport.value = item;
  currentRecordMapping.value = item.recordMapping;
  recordMappingModalActive.value = true;
}

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
}>({
  query: '',
  column: undefined,
  order: undefined,
});

function onSaveRecordMapping(jsonString: string) {
  if (currentRepresentativeImport.value) {
    currentRepresentativeImport.value.recordMapping = jsonString;
    currentRepresentativeImport.value.recordMode = RecordMode.Modified;
    onSave(currentRepresentativeImport.value);
  }
}
</script>
